import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IconsManagerService {
  readonly #http = inject(HttpClient);

  getFontAwesomeIcons(): Observable<Record<string, IconEntry[]>> {
    return this.#http.get('/assets/fontawesome/icons.yml', { responseType: 'text' }).pipe(
      map((rawYaml) => {
        const entries: Record<string, IconEntry[]> = {};
        const blockRegex = /^\s*['"]?([a-zA-Z0-9-]+)['"]?:\s*\n((?: {2,}.+\n?)*)/gm;

        let blockMatch: RegExpExecArray | null;

        while ((blockMatch = blockRegex.exec(rawYaml)) !== null) {
          const key = blockMatch[1];
          const blockContent = blockMatch[2];

          const labelRegex = /^\s+label:\s+(.*)$/m;
          const labelMatch = labelRegex.exec(blockContent);
          const label = labelMatch ? labelMatch[1].trim() : '';

          const stylesRegex = /^\s+styles:\s*\n((?:\s+-\s+.*\n?)+)/m;
          const stylesMatch = stylesRegex.exec(blockContent);
          const styles: string[] = [];

          if (stylesMatch) {
            const styleLines = stylesMatch[1].trim().split('\n');
            for (const line of styleLines) {
              const styleRegex = /-\s+(.*)/;
              const style = styleRegex.exec(line);
              if (style) {
                styles.push(style[1].trim());
              }
            }
          }

          const searchRegex = /^\s+terms:\s*\n((?:\s+-\s+.*\n?)+)/m;
          const searchMatch = searchRegex.exec(blockContent);
          const searchTerms: string[] = [];

          if (searchMatch) {
            const termLines = searchMatch[1].trim().split('\n');
            for (const line of termLines) {
              const termRegex = /-\s+(.*)/;
              const term = termRegex.exec(line);
              if (term) searchTerms.push(term[1].trim());
            }
          }

          if (label && styles.length) {
            for (const s of styles) {
              const iconEntry: IconEntry = { id: `fa-${s} fa-${key}`, displayValue: label, searchTerms };
              if (!entries[s]) {
                entries[s] = [];
              }
              entries[s].push(iconEntry);
            }
          }
        }

        return entries;
      })
    );
  }

  getIcomoonIcons(): Observable<IconEntry[]> {
    return this.#http.get<any>('assets/icomoon/selection.json', { responseType: 'json' }).pipe(
      map((selection) => {
        const prefix = selection.preferences.fontPref.prefix;
        return selection.icons.map(
          (icon: any) =>
            ({
              id: `${prefix}${icon.properties.name}`,
              displayValue: icon.properties.name,
              searchTerms: Array.isArray(icon.icon.tags) ? icon.icon.tags : [],
            }) as IconEntry
        );
      })
    );
  }
}

export type IconEntry = { id: string; displayValue: string; searchTerms: string[] };
